import { catchError, of, switchMap } from "rxjs";
import {
    hasAvailableNow,
    hasReadyToBuild,
    LocationType,
    hasInventoryStatusCombination,
} from "features/searchResultsFacets";
import ListingsResultTypeLookUp from "utils/resultCategoryTypeMapping";
import StringExtensions from "utils/stringExtensions";
import saveSearchInformation$ from "../apis/create";
import NotificationFrequencyType from "../enums/notificationFrequencyType";
import ResultCategoryType from "../enums/resultCategoryType";
import OtherFiltersType from "../../searchResultsFacets/enums/otherFiltersType";

/**
 * A dictionary mapping filter keys to user-friendly names.
 */
const friendlyNamesDictionary = {
    active: "Active",
    adult: "55+",
    agerestricted: "Age-Restricted",
    closeout: "Closeout",
    comingsoon: "Coming Soon",
    condosandtownhomes: "Condos & Townhomes",
    consumerpromos: "Consumer Promotions",
    customhomes: "Custom",
    exclude55: "Exclude 55+",
    excludeagerestricted: "Exclude Age-Restricted",
    gatedcommunity: "Gated Community",
    golfcourse: "Golf Course",
    grandopening: "Grand Opening",
    green: "Green Spaces",
    hashomeownersassociationfee: "Has HOA Fee",
    hotdeals: "Hot Deals",
    livingareas: "Living Areas",
    luxury: "Luxury",
    manufactured: "Manufactured",
    marketingstatus: "Marketing Status",
    modelhomes: "Model Homes",
    moveindate: "Expected Move-In",
    natureareas: "Nature Areas",
    parks: "Parks",
    pool: "Pool",
    pricereduction: "Price Reduction",
    primarybedroom: "Primary Bedroom",
    reviews: "Customer Reviews",
    rvgarage: "RV Garage",
    singlefamily: "Single-Family",
    sportsfacilities: "Sports Facilities",
    stories: "Stories",
    views: "Scenic Views",
    waterfront: "Waterfront",
};

const numeralFilters = {
    bathrooms: { singular: "Bathroom", plural: "Bathrooms" },
    bedrooms: { singular: "Bedroom", plural: "Bedrooms" },
    garages: { singular: "Garage", plural: "Garages" },
};
/**
 * Constructs a location text string based on the provided location context.
 *
 * @param {Object} locationContext - The context of the location.
 * @param {string} locationContext.marketName - The market name.
 * @param {string} locationContext.county - The county name.
 * @param {string} locationContext.city - The city name.
 * @param {string} locationContext.syntheticName - A synthetic name for the location.
 * @param {string} locationContext.stateAbbreviation - The state abbreviation.
 * @param {string} locationContext.zip - The ZIP code.
 * @returns {string} A formatted location text string.
 */
const getLocationText = (locationContext) => {
    const {
        marketName,
        county,
        city,
        syntheticGeographicName,
        marketState,
        zip,
    } = locationContext;

    return [`${marketName}, ${marketState}`, syntheticGeographicName, county, city, zip]
        .filter(Boolean)
        .join(', ');
};

/**
 * Constructs a neighborhood text string based on the provided location context.
 *
 * @param {Object} locationContext - The context of the location.
 * @param {string} locationContext.neighborhood - The neighborhood name.
 * @param {string} locationContext.city - The city name.
 * @param {string} locationContext.marketState - The state abbreviation.
 * @returns {string} A formatted location text string.
 */
const getNeighborhoodText = (locationContext) => {
    const {
        neighborhood,
        city,
        marketState,
    } = locationContext;

    const neighborhoodText = StringExtensions.toCapitalizedCase(LocationType.Neighborhood.toString())
    return [`${neighborhood} ${neighborhoodText}`, city, marketState]
        .filter(Boolean)
        .join(', ');
};

/**
 * Constructs a school district text string based on the provided location context.
 *
 * @param {Object} locationContext - The context of the location.
 * @param {string} locationContext.schoolDistrictName - The school district name.
 * @param {string} locationContext.marketState - The state abbreviation.
 * @returns {string} A formatted location text string.
 */
const getSchoolDistrictText = (locationContext) => {
    const {
        schoolDistrictName,
        marketState,
    } = locationContext;

    return [`${schoolDistrictName}`, marketState]
        .filter(Boolean)
        .join(', ');
};

/**
 * Constructs a market text string based on the provided location context.
 *
 * @param {Object} locationContext - The context of the location.
 * @param {string} locationContext.marketName - The market name.
 * @param {string} locationContext.marketState - The state abbreviation.
 * @returns {string} A formatted market text string suffixed with " Area".
 */
const getMarketText = (locationContext) => {
  const {
    marketName,
    marketState
  } = locationContext;

  const marketParts = [marketName, marketState].filter(Boolean);
  const marketText = marketParts.join(', ');

  return `${marketText} Area`;
}

/**
 * Retrieves the value of a facet by its key, ignoring case sensitivity.
 *
 * @param {Object} facets - The facets object containing key-value pairs of facet data.
 * @param {string} key - The key of the facet to retrieve.
 * @returns {any} The value of the facet if found, or `null` if the key does not exist.
 */
const getFacetValue = (facets, key) => {
    const normalizedKey = key?.toLowerCase();
    const foundKey = Object.keys(facets).find(facet => facet.toLowerCase() === normalizedKey);
    return foundKey ? facets[foundKey] : null;
};

/**
 * Determines the key for the first filter applied based on the saved search parameters.
 *
 * @param {Object} savedSearchParameters - The parameters of the saved search.
 * @param {string} savedSearchParameters.firstFilterApplied - The first filter applied to the search.
 * @param {Object} savedSearchParameters.currentFacets - The current facets applied to the search.
 * @returns {string} The key for the first filter applied.
 */
const getFirstFilterAppliedKey = (savedSearchParameters) => {
    const { firstFilterApplied, currentFacets } = savedSearchParameters;

    if (firstFilterApplied === 'price') {
        if (currentFacets.maximumPrice && currentFacets.minimumPrice) {
            return "pricerange";
        }
        if (currentFacets.maximumPrice) {
            return "maximumprice";
        }

        return "minimumprice";
    }

    return firstFilterApplied;
};

/**
 * Determines the value for the first filter applied based on the saved search parameters.
 *
 * @param {Object} savedSearchParameters - The parameters of the saved search.
 * @param {string} savedSearchParameters.firstFilterApplied - The first filter applied to the search.
 * @param {Object} savedSearchParameters.currentFacets - The current facets applied to the search.
 * @returns {string} The value for the first filter applied.
 */
const getFirstFilterAppliedValue = (savedSearchParameters) => {
    const { firstFilterApplied, currentFacets } = savedSearchParameters;

    const filterHandlers = {
        price: () => {
            if (currentFacets.maximumPrice && currentFacets.minimumPrice) {
                return `${currentFacets.minimumPrice} - ${currentFacets.maximumPrice}`;
            }

            return currentFacets.maximumPrice || currentFacets.minimumPrice || '';
        },
        primarybedroom: () =>
            getFacetValue(currentFacets, 'primarybedroom') === 1 ? 'Up' : 'Down',
        builderbrand: () => getFacetValue(currentFacets, 'brandname'),
        moveindate: () => {
            if (hasInventoryStatusCombination(currentFacets.homeStatus)) {
                return 'Quick Move In';
            }

            if (hasAvailableNow(currentFacets.homeStatus)) {
                return 'Available Now';
            }

            if (hasReadyToBuild(currentFacets.homeStatus)) {
                return 'Ready to Build';
            }

            const moveInDateRange = getFacetValue(currentFacets, 'moveInDateRange');
            return moveInDateRange ? `${moveInDateRange} months` : '';
        },
    };

    return filterHandlers[firstFilterApplied]
        ? filterHandlers[firstFilterApplied]()
        : getFacetValue(currentFacets, firstFilterApplied);
};

/**
 * Constructs a filter text string based on the saved search parameters.
 *
 * @param {Object} savedSearchParameters - The parameters of the saved search.
 * @param {string} savedSearchParameters.firstFilterApplied - The first filter applied to the search.
 * @param {Object} savedSearchParameters.currentFacets - The current facets applied to the search.
 * @returns {string} A formatted filter text string.
 */
const getFilterText = (savedSearchParameters) => {
    const firstFilterAppliedKey = getFirstFilterAppliedKey(savedSearchParameters);
    const filter = friendlyNamesDictionary[firstFilterAppliedKey];
    const numeralFilter = numeralFilters[firstFilterAppliedKey];
    const filterValue = getFirstFilterAppliedValue(savedSearchParameters);

    if (savedSearchParameters.firstFilterApplied === OtherFiltersType.Price.toString() ||
        savedSearchParameters.firstFilterApplied === OtherFiltersType.BuilderBrand.toString()) {
        return ` - ${filterValue}`;
    }

    if (!filter && !numeralFilter) {
        return '';
    }

    if (typeof filterValue === 'number' && numeralFilter) {
        const filterName = filterValue === 1 ? numeralFilter.singular : numeralFilter.plural;
        return ` - ${filterValue} ${filterName}`;
    }

    const valueText = (typeof filterValue === 'boolean') ? '' : `${filterValue} `;
    return ` - ${valueText}${filter}`;
};

/**
 * Generates a user-friendly name for the saved search based on its parameters.
 *
 * @param {Object} savedSearchParameters - The parameters of the saved search.
 * @returns {string} The name of the saved search.
 */
export const getSavedSearchName = (savedSearchParameters) => {
    const { searchParameters, resultType } = savedSearchParameters;
    const capitalizedResultType = StringExtensions.toCapitalizedCase(resultType);

    switch (searchParameters.location.locationType) {
        case LocationType.RectangularArea.toString():
            return `Map Search${getFilterText(savedSearchParameters)} | ${capitalizedResultType}`;
        case LocationType.Neighborhood.toString():
            return `${getNeighborhoodText(searchParameters.location)}${getFilterText(savedSearchParameters)} | ${capitalizedResultType}`;
        case LocationType.SchoolDistrict.toString():
            return `${getSchoolDistrictText(searchParameters.location)}${getFilterText(savedSearchParameters)} | ${capitalizedResultType}`;
        case LocationType.Market.toString():
            return `${getMarketText(searchParameters.location)}${getFilterText(savedSearchParameters)} | ${capitalizedResultType}`;
        default:
            return `${getLocationText(searchParameters.location)}${getFilterText(savedSearchParameters)} | ${capitalizedResultType}`;
    }
};

/**
 * Generates the payload for saving a search based on the provided parameters.
 *
 * @param {Object} savedSearchParameters - The parameters for the saved search.
 * @param {string} savedSearchParameters.resultType - The type of result for the saved search.
 * @param {string} savedSearchParameters.userId - The ID of the user saving the search.
 * @returns {Object} The payload object to be used for saving the search.
 */
const getSaveSearchPayload = (savedSearchParameters) => {
    const { resultType, userId, currentUrl, searchParameters } = savedSearchParameters;
    const payload = {
        SearchName: getSavedSearchName(savedSearchParameters),
        NotificationFrequency: NotificationFrequencyType.OnPublishedListings.toString(),
        UserId: userId,
        ListingResultType: ResultCategoryType.fromKey(ListingsResultTypeLookUp[resultType]).toString(),
        UserSavedSearchUrl: currentUrl,
        SearchCommand: searchParameters,
    };

    return payload;
};

/**
 * Saves the search by sending the payload to the backend API.
 *
 * @param {Object} searchParameters - An object containing the search parameters.
 * @returns {Observable} An observable that emits the result of the save operation.
 * Emits `null` if an error occurs during the save operation.
 */
export const saveSearch$ = (searchParameters) => {
    const payload = getSaveSearchPayload(searchParameters);

    return saveSearchInformation$(payload).pipe(
        catchError(() => of(null)),
        switchMap(() => of(null))
    );
};